"use client";

import * as React from "react";
import Image from "next/image";
import { useDropzone } from "react-dropzone";

import { Camera, ImagePlus, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { fieldVariantClasses, type FieldVariant } from "./field-variants";
import { LiquidPill } from "./liquid-pill";

export interface ImageUploaderV2File {
  /** Present for a newly selected local file; omitted for an existing remote preview. */
  file?: File;
  preview: string;
  name: string;
}

export interface ImageUploaderV2Props {
  value?: ImageUploaderV2File | null;
  onChange?: (value: ImageUploaderV2File | null) => void;
  /** Extensiones separadas por coma, p. ej. `.png,.jpg,.webp` */
  accept?: string;
  size?: "sm" | "md" | "lg";
  variant?: FieldVariant;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  changeLabel?: string;
  emptyHint?: string;
  "aria-label"?: string;
}

const SIZE_CONFIG = {
  sm: {
    box: "h-20 w-20",
    icon: 18,
    radius: "rounded-xl",
    hint: "text-[9px]",
  },
  md: {
    box: "h-[100px] w-[100px]",
    icon: 22,
    radius: "rounded-2xl",
    hint: "text-[10px]",
  },
  lg: {
    box: "h-28 w-28",
    icon: 26,
    radius: "rounded-2xl",
    hint: "text-[11px]",
  },
} as const;

const DEFAULT_ACCEPT = ".png,.jpg,.jpeg,.gif,.svg,.webp";

/** react-dropzone@12 expects string | string[] (not the v14 Accept object). */
function parseAccept(accept: string): string[] {
  return accept
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function ImageUploaderV2({
  value = null,
  onChange,
  accept = DEFAULT_ACCEPT,
  size = "md",
  variant = "violet",
  label,
  helperText,
  error,
  disabled = false,
  changeLabel = "Cambiar",
  emptyHint,
  "aria-label": ariaLabel = "Subir imagen",
}: ImageUploaderV2Props) {
  const inputId = React.useId();
  const [isReading, setIsReading] = React.useState(false);
  const sizeConfig = SIZE_CONFIG[size];
  const colors = fieldVariantClasses(variant, { error: Boolean(error) });
  const hasPreview = Boolean(value?.preview);

  const handleDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[acceptedFiles.length - 1];
      if (!file || !onChange) return;

      setIsReading(true);
      try {
        const preview = await readFileAsDataUrl(file);
        onChange({ file, preview, name: file.name });
      } finally {
        setIsReading(false);
      }
    },
    [onChange]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } =
    useDropzone({
      // v12: string[] accept. Disable FS Access API — object/empty accept makes
      // showOpenFilePicker throw TypeError that dropzone swallows (click = no-op).
      accept: parseAccept(accept),
      useFsAccessApi: false,
      multiple: false,
      disabled: disabled || isReading,
      noClick: hasPreview,
      noKeyboard: false,
      onDrop: handleDrop,
    });

  const handleRemove = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.(null);
  };

  const handleReplace = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    open?.();
  };

  return (
    <div className="w-full">
      {label && (
        <p
          className={cn(
            "mb-2 px-0.5 text-xs font-medium",
            error ? "text-destructive" : "text-slate-600 dark:text-zinc-400"
          )}
        >
          {label}
        </p>
      )}

      <div
        {...getRootProps({
          // Avoid nested button semantics when preview shows its own Cambiar control.
          role: hasPreview ? "group" : "button",
          className: cn(
            "group/uploader relative shrink-0 outline-none",
            sizeConfig.box,
            disabled && "pointer-events-none opacity-50"
          ),
        })}
      >
        <input
          {...getInputProps({
            id: inputId,
            "aria-label": ariaLabel,
          })}
        />

        <div
          className={cn(
            "relative h-full w-full overflow-hidden border bg-white/70 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-300 dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)]",
            sizeConfig.radius,
            colors.border,
            isDragActive &&
              !isDragReject &&
              cn("scale-[1.02]", colors.focusBorder, colors.focusRing, "ring-2 ring-offset-0"),
            isDragReject && "border-destructive ring-2 ring-destructive/20",
            !hasPreview &&
              "cursor-pointer hover:-translate-y-0.5 hover:border-indigo-300/90 hover:shadow-[0_10px_28px_-16px_rgba(79,70,229,0.45)] dark:hover:border-indigo-700/80"
          )}
        >
          {!hasPreview && (
            <>
              <div
                aria-hidden
                className={cn(
                  "absolute inset-0 bg-gradient-to-br from-indigo-50/90 via-violet-50/50 to-sky-50/70 dark:from-indigo-950/50 dark:via-violet-950/30 dark:to-sky-950/20",
                  sizeConfig.radius
                )}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -right-3 -top-3 h-10 w-10 rounded-full bg-indigo-400/15 blur-xl transition-opacity duration-300 group-hover/uploader:opacity-100 dark:bg-indigo-500/20"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-4 -left-2 h-12 w-12 rounded-full bg-violet-400/10 blur-xl dark:bg-violet-500/15"
              />

              <div className="relative flex h-full flex-col items-center justify-center gap-1.5 px-2 text-center">
                <div
                  className={cn(
                    "flex items-center justify-center rounded-full border border-white/70 bg-white/80 shadow-[0_8px_20px_-12px_rgba(79,70,229,0.55)] backdrop-blur-sm dark:border-white/10 dark:bg-zinc-900/80",
                    size === "sm" ? "h-9 w-9" : "h-11 w-11"
                  )}
                >
                  {isReading ? (
                    <Sparkles
                      className="h-4 w-4 animate-pulse text-indigo-500 dark:text-indigo-300"
                      size={sizeConfig.icon}
                      strokeWidth={1.75}
                    />
                  ) : isDragActive ? (
                    <ImagePlus
                      className="h-4 w-4 text-indigo-600 dark:text-indigo-300"
                      size={sizeConfig.icon}
                      strokeWidth={1.75}
                    />
                  ) : (
                    <Camera
                      className="h-4 w-4 text-indigo-600 transition-transform duration-300 group-hover/uploader:scale-110 dark:text-indigo-300"
                      size={sizeConfig.icon}
                      strokeWidth={1.75}
                    />
                  )}
                </div>

                {emptyHint && (
                  <span
                    className={cn(
                      "max-w-[88%] font-medium leading-tight text-indigo-700/80 dark:text-indigo-200/80",
                      sizeConfig.hint
                    )}
                  >
                    {isDragActive ? "Suelta aquí" : emptyHint}
                  </span>
                )}
              </div>
            </>
          )}

          {hasPreview && value?.preview && (
            <>
              <Image
                src={value.preview}
                alt=""
                fill
                unoptimized
                className="pointer-events-none object-cover"
                sizes="120px"
              />

              {/* Dim veil: never captures clicks (opacity-0 parent was eating the Cambiar hit-target). */}
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-0 bg-slate-950/0 opacity-0 transition-all duration-300 group-hover/uploader:bg-slate-950/45 group-hover/uploader:opacity-100 group-focus-within/uploader:bg-slate-950/45 group-focus-within/uploader:opacity-100",
                  sizeConfig.radius
                )}
              />

              <div className="absolute inset-0 z-[1] flex items-center justify-center">
                <button
                  type="button"
                  onClick={handleReplace}
                  className="rounded-full border border-white/40 bg-black/55 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-sm transition hover:bg-black/70"
                >
                  {changeLabel}
                </button>
              </div>

              <div className="absolute right-1.5 top-1.5 z-10">
                <LiquidPill
                  as="button"
                  type="button"
                  variant="rose"
                  appearance="tinted"
                  shape="circle"
                  size="xs"
                  icon={<X className="h-3 w-3" strokeWidth={2} />}
                  onClick={handleRemove}
                  aria-label="Quitar imagen"
                />
              </div>
            </>
          )}

          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 border border-white/50 dark:border-white/5",
              sizeConfig.radius
            )}
          />
        </div>
      </div>

      {(error ?? helperText) && (
        <p
          className={cn(
            "mt-1.5 px-0.5 text-xs",
            error ? "text-destructive" : "text-muted-foreground"
          )}
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  );
}
